import type { HandoffRow, HandoffSource } from '../../api/sourcing';
import { displayCompanyName } from './labels';
import { brandKey, domainKeyOf } from './playGroups';

/** One company on "Companies to chase": its legal entities folded into one row, as Plays does. */
export interface SourcingGroup {
  /** The strongest entity: its name leads, and the row keys on it. */
  primary: HandoffRow;
  /** Every entity, strongest first (the primary included). */
  entities: HandoffRow[];
  /** Distinct display names, strongest first. */
  names: string[];
  countries: string[];
  sourceTypes: string[];
  /** Strongest entity's strength: entities are not summed, the cap means one company's case. */
  totalStrength: number;
  lastSeenAt: string;
}

/**
 * Rows → one group per company: two rows belong together when they share a
 * parent brand once legal forms and country, group and practice words are
 * dropped (brandKey, the rule Plays uses: "Randstad", "Randstad Uitzendbureau
 * b.v.", "RANDSTAD BELGIUM NV" and "Randstad Professional Solutions GmbH & Co.
 * KG" are one), or a company domain. Order: strongest group first.
 */
export function groupSourcingRows(rows: readonly HandoffRow[]): SourcingGroup[] {
  const sorted = [...rows].sort((a, b) => b.totalStrength - a.totalStrength);
  const parent = sorted.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const seen = new Map<string, number>();
  sorted.forEach((r, i) => {
    const brand = brandKey(r.name);
    const domain = domainKeyOf(r.domain);
    for (const k of [brand ? `b:${brand}` : null, domain ? `d:${domain}` : null]) {
      if (!k) continue;
      const j = seen.get(k);
      if (j === undefined) seen.set(k, i);
      else {
        const ra = find(i);
        const rb = find(j);
        if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
      }
    }
  });
  const groups = new Map<number, SourcingGroup>();
  sorted.forEach((r, i) => {
    const root = find(i);
    let g = groups.get(root);
    if (!g) {
      g = { primary: r, entities: [], names: [], countries: [], sourceTypes: [], totalStrength: r.totalStrength, lastSeenAt: r.lastSeenAt };
      groups.set(root, g);
    }
    g.entities.push(r);
    const name = displayCompanyName(r.name);
    if (!g.names.some((n) => n.toLowerCase() === name.toLowerCase())) g.names.push(name);
    if (r.country && !g.countries.includes(r.country)) g.countries.push(r.country);
    for (const t of r.sourceTypes) if (!g.sourceTypes.includes(t)) g.sourceTypes.push(t);
    if (r.lastSeenAt > g.lastSeenAt) g.lastSeenAt = r.lastSeenAt;
  });
  return [...groups.values()].sort((a, b) => b.totalStrength - a.totalStrength);
}

/** A buyer's name up to its first comma: "Land Hessen, vertreten durch …" → "Land Hessen". */
function shortBuyer(name: unknown): string | null {
  if (typeof name !== 'string' || !name.trim()) return null;
  const cut = name.split(/,\s|\s+-\s+|\s+–\s+/)[0]!.trim();
  return cut.length > 60 ? `${cut.slice(0, 57).trimEnd()}…` : cut;
}

/**
 * One sentence per reason, for the "Why it's a lead" column (review #11):
 * the stored summaries run a TED title, a buyer and a count into one line
 * ("…Maintenance des…" around April 2027 … (+561 more re-tenders in reach)),
 * and splitting them into chips broke one sentence into fragments. Re-tenders
 * are rebuilt from their structured detail (buyer, end month, incumbent,
 * basis); the other sources keep their headline clause. The full stored text
 * stays on hover and when the row is opened.
 */
export function reasonLine(source: HandoffSource): string | null {
  const summary = source.summary ?? null;
  const detail = (source.detail ?? {}) as Record<string, unknown>;
  const primary = (detail.primary ?? null) as Record<string, unknown> | null;
  const contracts = Array.isArray(detail.contracts) ? detail.contracts.length : 0;
  if ((source.sourceType === 're_tender_incumbent' || source.sourceType === 're_tender_challenger') && primary) {
    const buyer = shortBuyer(primary.buyerName) ?? 'a public buyer';
    const end = String(primary.contractEndMonth ?? primary.contractEndDate ?? '').replace(/^around /, '');
    if (source.sourceType === 're_tender_incumbent') {
      const lead = contracts > 1 ? `Holds ${contracts} public contracts up for re-tender, the first for ${buyer}` : `Holds a public contract for ${buyer}`;
      return `${lead}${end ? `, ending ${end}` : ''}`;
    }
    const basis =
      typeof primary.challengerBasisText === 'string'
        ? primary.challengerBasisText
            .replace(/^(you|they) have won\b/, 'has won')
            .replace(/^(you|they) already sit\b/, 'already sits')
            .replace(/^(you|they) /, '')
        : null;
    return `Can challenge ${buyer}’s re-tender${end ? ` around ${end}` : ''}${
      typeof primary.incumbentName === 'string' ? ` (incumbent ${primary.incumbentName})` : ''
    }${basis ? `: ${basis}` : ''}`;
  }
  if (!summary) return null;
  // The headline before " — latest: …" / " — re-tender due": the count, not the notice.
  return summary.split(/\s+—\s+/)[0]!.replace(/\s*\(\+\d+ more [^)]*\)\s*$/, '').trim();
}
